<script setup>
import { ref } from 'vue';
import { API } from '@/services/index';
import { useQueryClient, useMutation } from '@tanstack/vue-query';
import { useUserStore } from '@/stores/user.store.js';
import { useAccountStore } from '@/stores/account.store.js';
import { useWorkspaceStore } from '@/stores/workspace.store.js';
import { GRANT_INVITE_WORKSPACE_USER_ROLE } from '@/services/workspace/mutations/workspace/index.js';
import defaultLogo from '@/assets/logo/PartnrUP_P_logo.png';
import BaseUserAvatar from '@/components/BaseUserAvatar.vue';
import { useSettingsStore } from '@/stores/settings.store.js';

// const formRules = {
//   emails: (value) => value.length > 0 || 'Email field is required.',
//   role: (value) => !!value || 'Role field is required.'
// };

const props = defineProps({
  isDisabled: {
    type: Boolean,
    default: false
  },
  accountRoles: {
    type: Array,
    default: () => [],
    required: true
  },
  member: {
    type: Object,
    default: () => [],
    required: true
  },
  workspaces: {
    type: Array,
    default: () => [],
    required: true
  }
});

const queryClient = useQueryClient();
const dialog = ref(false);
const activeAccount = computed(() => useSettingsStore().activeAccount);
const activeWorkspace = computed(() => useSettingsStore().activeWorkspace);
const isPermissionsLoading = computed(() => useUserStore().isPermissionsLoading);
const isFormValid = ref(false);
const selectedWorkspaces = ref([]);
const selectedAllWorkspaces = computed(() => selectedWorkspaces.value.length === props.workspaces.length);
const selectedSomeWorkspaces = computed(() => selectedWorkspaces.value.length > 0);

// onMounted(() => {
//   if (props.member?.selectedWorkspaces) {
//     selectedWorkspaces.value = props.member.selectedWorkspaces;
//     // selectedWorkspaces.value = props.member.workspaces.map((workspace) => workspace.workspace);
//   }
// });

const { isPending, isError, error, isSuccess, mutate } = useMutation({
  mutationFn: async (payload) => {
    return await API.sendQuery('/graphql/workspace', GRANT_INVITE_WORKSPACE_USER_ROLE, payload);
  },
  onSuccess: async (data) => {
    if (data.data.data.grantUserRole) {
      dialog.value = false;
      selectedWorkspaces.value = [];
      await queryClient.invalidateQueries({ queryKey: ['loadAccountTeamMembers'] });
      console.log('cleared query');
    }

    if (data.data.error) {
      console.log(data.data.error);
    }
  },
  onError: (error) => {
    console.log('error', error);
  }
});

const onSubmitHandler = async () => {
  const payload = {
    account_id: Number(activeAccount.value.id),
    user_id: props.member.user.id,
    role: props.member.role.name,
    workspace_ids: selectedWorkspaces.value.map((workspace) => Number(workspace.id))
  };

  await mutate(payload);
};

const selectAllWorkspaces = () => {
  if (selectedAllWorkspaces.value) {
    selectedWorkspaces.value = [];
  } else {
    selectedWorkspaces.value = props.workspaces;
  }
};
</script>

<template>
  <div class="tw-group tw-flex tw-cursor-pointer tw-items-center tw-space-x-2" @click="isPermissionsLoading || isDisabled ? '' : (dialog = true)">
    <v-btn
      width="25"
      height="25"
      color="primary-blue"
      idensity="compact"
      size="x-small"
      variant="outlined"
      icon="mdi-plus"
      class="tw-text-[0.7rem] tw-capitalize"
      :disabled="isPermissionsLoading || isDisabled"
    >
    </v-btn>
    <div class="tw-text-[0.8rem] tw-font-semibold tw-capitalize group-hover:tw-text-primary-blue">Add Workspace</div>
  </div>
  <v-dialog v-model="dialog" @update:modelValue="selectedWorkspaces = []" width="auto" attach="#app" :persistent="props.isLoading || isPending">
    <v-card class="tw-rounded-xl" max-width="702" width="702" :loading="isPending">
      <div class="tw-px-5 tw-pb-5">
        <h2 class="tw-my-3 tw-text-center tw-text-[0.79rem] tw-text-xl tw-font-bold tw-capitalize tw-text-primary-blue">Add Workspaces to:</h2>
        <div class="tw-mb-2 tw-text-center tw-text-[0.9rem]">
          {{ member.user.username && member.user.username.length > 0 ? member.user.username : member.user.email }}
        </div>
        <div class="tw-w-full tw-rounded-xl tw-bg-[#f7f7f7] tw-p-3 tw-pb-10">
          <v-form ref="inviteForm" v-model="isFormValid" @submit.prevent="onSubmitHandler">
            <div class="tw-flex tw-flex-col tw-space-y-5">
              <!--              <div class="tw-flex tw-w-full tw-items-center">-->
              <!--                <BaseUserAvatar-->
              <!--                  style="margin: 0 auto"-->
              <!--                  :text-styles="`cursor: pointer;font-size: 1.688rem; line-height: 64px;`"-->
              <!--                  :size="75"-->
              <!--                  :user="member.user"-->
              <!--                />-->
              <!--              </div>-->
              <div class="tw-flex tw-w-full tw-items-start tw-justify-between tw-space-x-3">
                <div class="tw-flex tw-w-full">
                  <v-select
                    v-model="selectedWorkspaces"
                    :items="workspaces"
                    width="455"
                    color="primary-blue"
                    variant="solo"
                    density="compact"
                    multiple
                    return-object
                    item-title="name"
                    placeholder="Select a workspace"
                    hide-details="auto"
                    closable-chips
                    :disabled="isPending"
                  >
                    <template v-slot:prepend-item>
                      <v-list-item @click="selectAllWorkspaces" title="Select All">
                        <template v-slot:prepend>
                          <v-checkbox-btn
                            :color="selectedSomeWorkspaces ? 'indigo-darken-4' : undefined"
                            :indeterminate="selectedSomeWorkspaces && !selectedAllWorkspaces"
                            :model-value="selectedAllWorkspaces"
                          ></v-checkbox-btn>
                        </template>
                      </v-list-item>

                      <v-divider class="mt-2"></v-divider>
                    </template>

                    <template v-slot:selection="{ item, index }">
                      <v-chip v-if="index < 2">
                        <v-avatar v-if="item.raw.image" size="16">
                          <v-img sizes="16" contain :src="item.raw.image" :alt="`${item.raw.name} logo`"></v-img>
                        </v-avatar>
                        <v-avatar v-else :rounded="false" size="16">
                          <v-img sizes="16" contain :src="defaultLogo" alt="default no-logo"></v-img>
                        </v-avatar>
                        <span style="font-size: 0.7rem; font-weight: 500; margin-left: 7px">{{ item.raw.name }}</span>
                      </v-chip>
                      <span v-if="index === 2" class="text-grey text-caption align-self-center">
                        (+{{ selectedWorkspaces.length - 2 }} workspaces)
                      </span>
                    </template>
                  </v-select>
                </div>
                <div class="tw-flex tw-w-full tw-justify-end">
                  <v-btn
                    type="submit"
                    :loading="isPending"
                    :disabled="isPending || !isFormValid"
                    class="tw-capitalize"
                    width="141"
                    color="primary-blue"
                  >
                    Add
                  </v-btn>
                </div>
              </div>
            </div>
          </v-form>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="css"></style>
